/**
 * The desk's own copy of the form while the guest holds the tablet.
 *
 * Deliberately shows every field, not only the ones the guest can reach: the
 * fields with no box on the page are exactly the ones the guest cannot see,
 * so this is the only place they can be filled in before the document is
 * stamped. Each change goes straight to the tablet, which applies it live.
 */

import { Loader2, ScanLine } from 'lucide-react'
import type { SessionField, SigningSession } from '@/lib/types'
import { FieldControl, pickerFallbackNote } from '@/components/domain/FieldControl'
import { cn } from '@/lib/utils'

export function MirrorFields({
  session,
  value,
  onChange,
  saving,
  readOnly,
}: {
  session: SigningSession
  value: (fieldKey: string, stored: string) => string
  onChange: (fieldKey: string, value: string) => void
  saving: boolean
  readOnly: boolean
}) {
  if (session.fields.length === 0) return null

  return (
    // On wide screens the card takes all the side panel's spare height, so the
    // list scrolls inside it instead of stopping at a fixed height.
    <div className="flex min-h-0 flex-col rounded-[12px] border border-[var(--border)] xl:min-h-48 xl:flex-1">
      <div className="flex items-center gap-1.5 border-b border-[var(--border)] px-3 py-2 text-[12px] font-medium text-[var(--text-muted)]">
        <ScanLine className="h-3.5 w-3.5" aria-hidden />
        {readOnly ? 'What the guest is filling in' : 'Correct a value'}
        {saving && <Loader2 className="ms-auto h-3 w-3 animate-spin" aria-hidden />}
      </div>

      <div className="flex max-h-72 min-h-0 flex-col gap-2 overflow-y-auto p-2.5 xl:max-h-none xl:flex-1">
        {session.fields.map((field) => (
          <Row
            key={field.field_key}
            field={field}
            value={value(field.field_key, field.value)}
            onChange={(next) => onChange(field.field_key, next)}
            readOnly={readOnly}
          />
        ))}
      </div>
    </div>
  )
}

function Row({
  field,
  value,
  onChange,
  readOnly,
}: {
  field: SessionField
  value: string
  onChange: (value: string) => void
  readOnly: boolean
}) {
  const note = pickerFallbackNote(field, value)
  return (
    <div>
      <label
        htmlFor={`mirror-${field.field_key}`}
        className="flex items-center gap-1.5 text-[11px] font-medium text-[var(--text-muted)]"
      >
        {field.label}
        {field.required && <span className="text-[var(--danger)]">*</span>}
        {!field.overlay && (
          <span className="text-[10px] font-normal">· not on the page, fill it in here</span>
        )}
      </label>
      <FieldControl
        id={`mirror-${field.field_key}`}
        spec={field}
        value={value}
        onChange={onChange}
        disabled={readOnly}
        className={cn('mt-0.5 h-8 text-[13px]', field.needs_review && 'border-[var(--warning)]')}
      />
      {note && <p className="mt-0.5 text-[10px] text-[var(--warning)]">{note}</p>}
    </div>
  )
}
